<template>
  <AdminLayout>
    <div class="space-y-8">
      <!-- Page Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white mb-2">User Management</h1>
        <p class="text-slate-400">Manage users, roles, and permissions across the platform</p>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Users</p>
              <p class="text-3xl font-bold text-white">{{ Number(stats.totalUsers || 0).toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-2">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                  </svg>
                  +12% from last month
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Active Users</p>
              <p class="text-3xl font-bold text-white">{{ Number(stats.activeUsers || 0).toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-2">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                  </svg>
                  +8% from last month
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">New This Month</p>
              <p class="text-3xl font-bold text-white">{{ stats.newUsers }}</p>
              <p class="text-sm text-emerald-400 mt-2">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                  </svg>
                  +24% from last month
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Admin Users</p>
              <p class="text-3xl font-bold text-white">{{ stats.adminUsers }}</p>
              <p class="text-sm text-slate-400 mt-2">System administrators</p>
            </div>
            <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div class="flex items-center space-x-4">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search users..."
              class="w-64 pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
            <svg class="absolute left-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <select
            v-model="filterRole"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="user">User</option>
          </select>
        </div>
        <button
          @click="showCreateModal = true"
          class="btn-hero-primary"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          Add User
        </button>
      </div>

      <!-- Users Table -->
      <div class="admin-stat-card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-700/50">
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">User</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Role</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Status</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Last Active</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in filteredUsers" :key="user.id" class="border-b border-slate-700/30 hover:bg-slate-800/20 transition-colors">
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-3">
                    <div class="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center text-slate-900 font-semibold">
                      {{ (user.name || user.email || 'U').charAt(0) }}
                    </div>
                    <div>
                      <p class="font-semibold text-white">{{ user.name }}</p>
                      <p class="text-sm text-slate-400">{{ user.email }}</p>
                    </div>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="{
                          'bg-purple-100 text-purple-800': user.role === 'admin',
                          'bg-blue-100 text-blue-800': user.role === 'manager',
                          'bg-gray-100 text-gray-800': user.role === 'user'
                        }">
                    {{ user.role.charAt(0).toUpperCase() + user.role.slice(1) }}
                  </span>
                </td>
                <td class="py-4 px-6">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="{
                          'bg-emerald-100 text-emerald-800': user.status === 'active',
                          'bg-red-100 text-red-800': user.status === 'inactive',
                          'bg-yellow-100 text-yellow-800': user.status === 'pending'
                        }">
                    {{ user.status.charAt(0).toUpperCase() + user.status.slice(1) }}
                  </span>
                </td>
                <td class="py-4 px-6 text-slate-300">{{ formatDate(user.lastActive) }}</td>
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-2">
                    <button
                      @click="editUser(user)"
                      class="p-2 text-slate-400 hover:text-emerald-400 transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button
                      @click="deleteUser(user.id)"
                      class="p-2 text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create User Modal -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/90 backdrop-blur-xl rounded-2xl p-6 w-full max-w-md border border-slate-700/50">
        <h3 class="text-xl font-bold text-white mb-4">Add New User</h3>
        <form @submit.prevent="createUser" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Name</label>
            <input
              v-model="newUser.name"
              type="text"
              required
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Enter user name"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input
              v-model="newUser.email"
              type="email"
              required
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Enter email address"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Role</label>
            <select
              v-model="newUser.role"
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="showCreateModal = false"
              class="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn-hero-primary"
            >
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAdminStore } from '../../stores/adminStore'
import AdminLayout from './AdminLayout.vue'

const adminStore = useAdminStore()

// State
const stats = ref({
  totalUsers: 0,
  activeUsers: 0,
  newUsers: 0,
  adminUsers: 0
})

const users = ref<any[]>([])
const isLoading = ref(false)

// Reactive state
const searchQuery = ref('')
const filterRole = ref('')
const showCreateModal = ref(false)
const newUser = ref({
  name: '',
  email: '',
  role: 'user'
})

// Computed
const filteredUsers = computed(() => {
  let filtered = users.value

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    filtered = filtered.filter(user =>
      String(user.name || '').toLowerCase().includes(q) ||
      String(user.email || '').toLowerCase().includes(q)
    )
  }

  if (filterRole.value) {
    filtered = filtered.filter(user => user.role === filterRole.value)
  }

  return filtered
})

// Methods
const formatDate = (date: Date | string) => {
  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(parsed)
}

const loadUsers = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/admin/users', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      const rows = Array.isArray(data.users) ? data.users : []
      users.value = rows.map((user: any) => ({
        ...user,
        name: user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'User',
        role: user.role || user.userType || 'user',
        status: user.status || 'active',
        lastActive: user.lastActive || user.createdAt || new Date(),
      }))
      stats.value = {
        totalUsers: Number(data.stats?.totalUsers ?? users.value.length),
        activeUsers: Number(data.stats?.activeUsers ?? users.value.length),
        newUsers: Number(data.stats?.newUsers ?? 0),
        adminUsers: Number(data.stats?.adminUsers ?? users.value.filter((u: any) => u.role === 'admin' || u.userType === 'admin').length),
      }
    } else {
      // Load mock data for demo
      loadMockData()
    }
  } catch (error) {
    console.error('Error loading users:', error)
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  users.value = [
    { id: 1, name: 'John Smith', email: 'john@oilbuddy.com', role: 'admin', status: 'active', lastActive: new Date('2024-01-15') },
    { id: 2, name: 'Sarah Johnson', email: 'sarah@contractor.com', role: 'manager', status: 'active', lastActive: new Date('2024-01-14') },
    { id: 3, name: 'Mike Wilson', email: 'mike@company.com', role: 'user', status: 'active', lastActive: new Date('2024-01-13') },
    { id: 4, name: 'Emily Davis', email: 'emily@oilfield.com', role: 'user', status: 'inactive', lastActive: new Date('2024-01-10') },
    { id: 5, name: 'Robert Brown', email: 'robert@drilling.com', role: 'manager', status: 'pending', lastActive: new Date('2024-01-12') }
  ]
  
  stats.value = {
    totalUsers: 1247,
    activeUsers: 892,
    newUsers: 156,
    adminUsers: 8
  }
}

const editUser = async (user: any) => {
  // TODO: Implement edit user functionality
  console.log('Edit user:', user)
}

const deleteUser = async (userId: number) => {
  if (!confirm('Are you sure you want to delete this user?')) return
  
  try {
    const response = await fetch(`/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      users.value = users.value.filter(user => user.id !== userId)
      stats.value.totalUsers--
    } else {
      console.error('Failed to delete user')
    }
  } catch (error) {
    console.error('Error deleting user:', error)
  }
}

const createUser = async () => {
  try {
    const response = await fetch('/api/admin/users', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newUser.value)
    })
    
    if (response.ok) {
      const user = await response.json()
      users.value.push(user)
      stats.value.totalUsers++
      stats.value.newUsers++
      showCreateModal.value = false
      newUser.value = { name: '', email: '', role: 'user' }
    } else {
      console.error('Failed to create user')
    }
  } catch (error) {
    console.error('Error creating user:', error)
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.btn-hero-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}
</style>
